import { useState } from 'react';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, ConfirmDialog } from '@shared/ui';
import type { ManufacturerProfile } from '@entities/manufacturer';
import type { KycStatus } from '@entities/user';
import { useApproveKyc, useRejectKyc } from '../model/use-kyc-actions';

const kycVariant: Record<KycStatus, 'secondary' | 'warning' | 'success' | 'destructive'> = {
  NONE: 'secondary',
  PENDING: 'warning',
  VERIFIED: 'success',
  REJECTED: 'destructive',
};

export function KycActionsPanel({ profile }: { profile: ManufacturerProfile }) {
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const approve = useApproveKyc(profile.id);
  const reject = useRejectKyc(profile.id);

  const isRejected = profile.kycStatus === 'REJECTED';
  const isVerified = profile.kycStatus === 'VERIFIED';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>KYC</span>
          <Badge variant={kycVariant[profile.kycStatus]} className="text-sm">
            {profile.kycStatus}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isRejected && profile.kycRejectionReason ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
            <div className="font-medium text-destructive">Причина отказа</div>
            <p className="mt-1 text-foreground/80">{profile.kycRejectionReason}</p>
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          {!isVerified ? (
            <Button variant="success" onClick={() => setApproveOpen(true)}>
              Подтвердить KYC
            </Button>
          ) : null}
          {!isRejected ? (
            <Button variant="destructive" onClick={() => setRejectOpen(true)}>
              {isVerified ? 'Отозвать (отклонить)' : 'Отклонить KYC'}
            </Button>
          ) : null}
        </div>
        <p className="text-[11px] text-muted-foreground">
          Ручной онбординг: approve доступен из любого статуса — не требует, чтобы вендор жал
          «отправить на проверку».
        </p>
      </CardContent>

      <ConfirmDialog
        open={approveOpen}
        onOpenChange={setApproveOpen}
        title="Одобрить KYC?"
        description={`Компания «${profile.companyName}» получит статус VERIFIED и email-уведомление.`}
        confirmLabel="Approve"
        variant="success"
        isPending={approve.isPending}
        onConfirm={() => approve.mutate(undefined, { onSuccess: () => setApproveOpen(false) })}
      />
      <ConfirmDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="Отклонить KYC?"
        description="Причина будет видна производителю и записана в audit log."
        confirmLabel="Reject"
        variant="destructive"
        withReason
        reasonLabel="Причина отказа"
        isPending={reject.isPending}
        onConfirm={(reason) => reject.mutate(reason, { onSuccess: () => setRejectOpen(false) })}
      />
    </Card>
  );
}
